import { CapacitorHttp, type HttpOptions } from "@capacitor/core";
import { DateTime } from "luxon";
import { trackEvent } from "./analytics";
import { baseURL } from "./networkWrapper";

/**
 * Planned maintenance is switched on by publishing `maintenance.json` at the site root:
 *
 *     { "active": true, "ends_at": "2026-10-04T02:00:00-04:00" }
 *
 * `ends_at` is optional and must carry an explicit offset (`Z` or `±hh:mm`), because the screen shows it in
 * the reader's own time zone. Removing the file, or setting `active` to anything but `true`, ends it.
 */

export type MaintenanceNotice = {
	endsAt: DateTime | null;
};

export enum MaintenanceNoticeState {
	/** The file is there and says maintenance is on. */
	Active = "active",
	/** Confirmed off: the host answered, and either has no file or its file doesn't switch maintenance on. */
	None = "none",
	/** The host couldn't be read (network error, timeout, a server error) — nothing is known either way. */
	Unknown = "unknown",
}

export type MaintenanceNoticeResult =
	| { state: MaintenanceNoticeState.Active; notice: MaintenanceNotice }
	| { state: MaintenanceNoticeState.None }
	| { state: MaintenanceNoticeState.Unknown };

export const MAINTENANCE_NOTICE_PATH = "/maintenance.json";
/** The first load waits this long for the file at most, and every later read gives up after it too. */
export const MAINTENANCE_NOTICE_TIMEOUT_MS = 3_000;
const CACHE_BUSTING_PARAM = "t";
const FIRST_SUCCESS_STATUS = 200;
const FIRST_REDIRECT_STATUS = 300;
const NOT_FOUND_STATUS = 404;
const EXPLICIT_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * The file sits beside the site, not under `/api`: `https://app.inhouse.health/api` resolves to
 * `https://app.inhouse.health/maintenance.json`. On a dev server the API's own 404 means "no maintenance".
 */
export const resolveMaintenanceNoticeUrl = (serverAddress: string): string => new URL(MAINTENANCE_NOTICE_PATH, serverAddress).toString();

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const parseEndsAt = (raw: unknown): DateTime | null => {
	if (raw === undefined || raw === null) return null;
	const parsed = typeof raw === "string" && EXPLICIT_OFFSET.test(raw) ? DateTime.fromISO(raw, { setZone: true }) : null;
	if (parsed?.isValid) return parsed;
	// DEFAULT: an unusable end time drops only the time — the maintenance itself is still real, so the screen
	// shows without the "back by" line rather than guessing a zone or hiding maintenance.
	trackEvent("maintenance_notice_invalid_ends_at", { ends_at: String(raw) });
	return null;
};

const decodeBody = (body: unknown): unknown => {
	if (typeof body !== "string") return body;
	try {
		return JSON.parse(body);
	} catch {
		// DEFAULT: an unparseable body is not our file — nginx answers a missing path with index.html and a 200.
		return null;
	}
};

/** A notice only for an object whose `active` is exactly `true`; anything else means no maintenance. */
export const parseMaintenanceNotice = (body: unknown): MaintenanceNotice | null => {
	const decoded = decodeBody(body);
	// DEFAULT: anything but `{ "active": true }` is no maintenance. Only an explicit switch may lock everyone out
	// of the app, so a typo (`"true"`, `1`) or a file some other tool wrote there never does.
	if (!isRecord(decoded) || decoded.active !== true) return null;
	return { endsAt: parseEndsAt(decoded.ends_at) };
};

const requestOptions = (signal: AbortSignal): HttpOptions => ({
	url: resolveMaintenanceNoticeUrl(baseURL),
	params: { [CACHE_BUSTING_PARAM]: String(DateTime.now().toMillis()) },
	connectTimeout: MAINTENANCE_NOTICE_TIMEOUT_MS,
	readTimeout: MAINTENANCE_NOTICE_TIMEOUT_MS,
	// Web only (the native side ignores it): the static host sends no CORS headers, so no cookies, no cache.
	webFetchExtra: { cache: "no-store", credentials: "omit", signal },
});

/**
 * Caps the read on every platform. The abort comes from a plain AbortController rather than
 * `AbortSignal.timeout`, which iOS WebViews before 16 don't have.
 */
const withDeadline = async <T>(makeRequest: (signal: AbortSignal) => Promise<T>): Promise<T> => {
	const controller = new AbortController();
	let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
	const deadline = new Promise<never>((_, reject) => {
		deadlineTimer = setTimeout(() => {
			controller.abort();
			reject(new Error(`maintenance notice exceeded ${MAINTENANCE_NOTICE_TIMEOUT_MS}ms`));
		}, MAINTENANCE_NOTICE_TIMEOUT_MS);
	});
	try {
		return await Promise.race([makeRequest(controller.signal), deadline]);
	} finally {
		clearTimeout(deadlineTimer);
	}
};

const NONE: MaintenanceNoticeResult = { state: MaintenanceNoticeState.None };
const UNKNOWN: MaintenanceNoticeResult = { state: MaintenanceNoticeState.Unknown };

const resultFromAnswer = (status: number, body: unknown): MaintenanceNoticeResult => {
	if (status === NOT_FOUND_STATUS) return NONE;
	if (status < FIRST_SUCCESS_STATUS || status >= FIRST_REDIRECT_STATUS) {
		// DEFAULT: any other non-2xx says nothing about the file, so it is unknown — not "maintenance over".
		return UNKNOWN;
	}
	const notice = parseMaintenanceNotice(body);
	return notice ? { state: MaintenanceNoticeState.Active, notice } : NONE;
};

/**
 * Reads the notice through `CapacitorHttp`, not `axiosInstance`: the app's interceptors add auth and client
 * headers that force a CORS preflight a static host can't answer. On native `CapacitorHttp` also bypasses
 * CORS, which a plain fetch can't.
 */
export const fetchMaintenanceNotice = async (): Promise<MaintenanceNoticeResult> => {
	try {
		const response = await withDeadline((signal) => CapacitorHttp.get(requestOptions(signal)));
		return resultFromAnswer(response.status, response.data);
	} catch (error) {
		// Not swallowed: reported here and returned as unknown, which each caller handles explicitly.
		trackEvent("maintenance_notice_fetch_failed", { error_message: error instanceof Error ? error.message : String(error) });
		return UNKNOWN;
	}
};
